import { fetchApi } from '../utils/api';
import type {
  TreatmentCategory,
  NGAPAct,
  DentalChartData,
  ClinicalTreatment,
  CreateTreatmentRequest,
  VisitRecord,
  MedicalRecord,
  PatientDocument,
  PatientRecordsSummary,
  ToothHistory,
  ToothData,
} from '../types/records';

// Re-export types for backward compatibility
export type {
  TreatmentCategory,
  NGAPAct,
  DentalChartData,
  ClinicalTreatment,
  CreateTreatmentRequest,
  VisitRecord,
  MedicalRecord,
  PatientDocument,
  PatientRecordsSummary,
  ToothHistory,
};

// Legacy type aliases
export type DentalChart = DentalChartData;
export type TreatmentRecord = ClinicalTreatment;

export interface CreateMedicalRecordRequest {
  patient_id: string;
  record_type: string;
  description: string;
  severity?: string;
  date?: string;
  notes?: string;
  is_active?: boolean;
}

export interface UploadDocumentRequest {
  patient_id: string;
  file: File;
  category: string;
  tooth_number?: number;
  notes?: string;
}

// ── Helper: extract data from response (#44 — typed + defensive) ──
function extractData<T>(response: unknown, key?: string): T {
  if (response == null) return response as T;
  const res = response as Record<string, unknown>;
  // Successful API envelope: { success: true, [key]: ... }
  if (res.success && key && res[key] !== undefined) return res[key] as T;
  if (res.success && res.data !== undefined) return res.data as T;
  if (res.success) return res as unknown as T;
  // Bare array (some endpoints)
  if (Array.isArray(response)) return response as T;
  // DRF pagination: { results: [...] }
  if (res.results !== undefined) return res.results as T;
  // Raw data envelope: { data: ... }
  if (res.data !== undefined) return res.data as T;
  return response as T;
}

export const recordsService = {

  // ━━━ NGAP Catalog ━━━

  async getNGAPCatalog(): Promise<TreatmentCategory[]> {
    try {
      const response = await fetchApi('records/api/ngap/catalog/');
      return extractData<TreatmentCategory[]>(response, 'categories');
    } catch (error) {
      console.error('[Records] Error fetching NGAP catalog:', error);
      return [];
    }
  },

  async getNGAPActs(params?: {
    category?: string;
    search?: string;
    chart_state?: string;
  }): Promise<NGAPAct[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.category) queryParams.set('category', params.category);
      if (params?.search) queryParams.set('search', params.search);
      if (params?.chart_state) queryParams.set('chart_state', params.chart_state);
      const qs = queryParams.toString();
      const url = `records/api/ngap/acts/${qs ? '?' + qs : ''}`;
      const response = await fetchApi(url);
      return extractData<NGAPAct[]>(response, 'acts');
    } catch (error) {
      console.error('[Records] Error fetching NGAP acts:', error);
      return [];
    }
  },

  // ━━━ Dental Chart ━━━

  async getDentalChart(patientId: string): Promise<DentalChartData | null> {
    try {
      const response = await fetchApi(`records/api/dental-chart/${patientId}/`);
      return extractData<DentalChartData>(response, 'dental_chart');
    } catch (error) {
      console.error('[Records] Error fetching dental chart:', error);
      return null;
    }
  },

  async updateDentalChart(
    patientId: string,
    teethData: Record<string, ToothData>
  ): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/dental-chart/${patientId}/update/`, {
      method: 'PUT',
      body: JSON.stringify({ teeth_data: teethData }),
    });
  },

  async updateTooth(
    patientId: string,
    toothNumber: number,
    toothData: ToothData
  ): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/dental-chart/${patientId}/tooth/`, {
      method: 'POST',
      body: JSON.stringify({ tooth_number: toothNumber, tooth_data: toothData }),
    });
  },

  // ━━━ Clinical Treatments ━━━

  async getTreatments(params?: {
    patient_id?: string;
    status?: string;
    tooth_number?: number;
    category?: string;
    limit?: number;
  }): Promise<ClinicalTreatment[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.patient_id) queryParams.set('patient_id', params.patient_id);
      if (params?.status) queryParams.set('status', params.status);
      if (params?.tooth_number) queryParams.set('tooth_number', String(params.tooth_number));
      if (params?.category) queryParams.set('category', params.category);
      if (params?.limit) queryParams.set('limit', String(params.limit));
      const qs = queryParams.toString();
      const url = `records/api/treatments/${qs ? '?' + qs : ''}`;
      const response = await fetchApi(url);
      return extractData<ClinicalTreatment[]>(response, 'treatments');
    } catch (error) {
      console.error('[Records] Error fetching treatments:', error);
      return [];
    }
  },

  async createTreatment(data: CreateTreatmentRequest): Promise<ClinicalTreatment> {
    const response = await fetchApi('records/api/treatments/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return extractData<ClinicalTreatment>(response, 'treatment');
  },

  async updateTreatment(id: string, data: Partial<CreateTreatmentRequest>): Promise<ClinicalTreatment> {
    const response = await fetchApi(`records/api/treatments/${id}/update/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return extractData<ClinicalTreatment>(response, 'treatment');
  },

  async completeTreatment(id: string, notes?: string): Promise<ClinicalTreatment> {
    const response = await fetchApi(`records/api/treatments/${id}/complete/`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    return extractData<ClinicalTreatment>(response, 'treatment');
  },

  async deleteTreatment(id: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/treatments/${id}/delete/`, {
      method: 'DELETE',
    });
  },

  // ━━━ Visit Records / Séances ━━━

  async getVisitRecords(patientId?: string): Promise<VisitRecord[]> {
    try {
      const url = patientId
        ? `records/api/visits/?patient_id=${patientId}`
        : 'records/api/visits/';
      const response = await fetchApi(url);
      return extractData<VisitRecord[]>(response, 'data') || [];
    } catch (error) {
      console.error('[Records] Error fetching visits:', error);
      return [];
    }
  },

  async getActiveSeance(patientId: string): Promise<VisitRecord | null> {
    try {
      const response = await fetchApi(`records/api/visits/active/?patient_id=${patientId}`);
      return extractData<VisitRecord | null>(response, 'data');
    } catch (error) {
      console.error('[Records] Error fetching active séance:', error);
      return null;
    }
  },

  async createVisitRecord(data: {
    patient_id: string;
    visit_type?: string;
    chief_complaint?: string;
    clinical_notes?: string;
  }): Promise<VisitRecord> {
    const response = await fetchApi('records/api/visits/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return extractData<VisitRecord>(response, 'data');
  },

  async closeSeance(visitId: string, amountPaid?: number): Promise<VisitRecord> {
    const body: Record<string, unknown> = {};
    if (amountPaid !== undefined) body.amount_paid = amountPaid;
    const response = await fetchApi(`records/api/visits/${visitId}/close/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return extractData<VisitRecord>(response, 'data');
  },

  async updateSeancePayment(visitId: string, amountPaid: number): Promise<VisitRecord> {
    const response = await fetchApi(`records/api/visits/${visitId}/update/`, {
      method: 'PUT',
      body: JSON.stringify({ amount_paid: amountPaid }),
    });
    return extractData<VisitRecord>(response, 'data');
  },

  // ━━━ Tooth History ━━━

  async getToothHistory(patientId: string, toothNumber: number): Promise<ToothHistory> {
    const response = await fetchApi(
      `records/api/tooth-history/${patientId}/${toothNumber}/`
    );
    return extractData<ToothHistory>(response);
  },

  // ━━━ Summary ━━━

  async getRecordsSummary(): Promise<PatientRecordsSummary[]> {
    try {
      const response = await fetchApi('records/api/summary/');
      const data = extractData<any>(response);
      if (Array.isArray(data)) return data;
      if (data?.summaries) return data.summaries;
      return [];
    } catch (error) {
      console.error('[Records] Error fetching summary:', error);
      return [];
    }
  },

  // ━━━ Medical Records ━━━

  async getMedicalRecords(patientId: string): Promise<MedicalRecord[]> {
    try {
      const response = await fetchApi(`records/api/medical-records/?patient_id=${patientId}`);
      return extractData<MedicalRecord[]>(response, 'records');
    } catch (error) {
      console.error('[Records] Error fetching medical records:', error);
      return [];
    }
  },

  async createMedicalRecord(
    data: CreateMedicalRecordRequest
  ): Promise<{ success: boolean; record_id?: string; message: string }> {
    return await fetchApi('records/api/medical-records/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteMedicalRecord(
    recordId: string
  ): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/medical-records/${recordId}/delete/`, {
      method: 'DELETE',
    });
  },

  // ━━━ Documents ━━━

  async uploadDocument(
    data: UploadDocumentRequest
  ): Promise<{ success: boolean; document_id?: string; message: string; data?: PatientDocument }> {
    const formData = new FormData();
    formData.append('patient_id', data.patient_id);
    formData.append('file', data.file);
    formData.append('category', data.category);
    if (data.tooth_number) formData.append('tooth_number', String(data.tooth_number));
    if (data.notes) formData.append('notes', data.notes);
    return await fetchApi('records/api/document/upload/', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  async getDocuments(
    patientId: string,
    category?: string
  ): Promise<PatientDocument[]> {
    let url = `records/api/documents/${patientId}/`;
    if (category) url += `?category=${category}`;
    const response = await fetchApi(url);
    return extractData<PatientDocument[]>(response);
  },

  async downloadDocument(documentId: string): Promise<void> {
    const tenant = localStorage.getItem('x-tenant') || window.location.hostname.split('.')[0];
    const response = await fetch(`/api/records/api/document/${documentId}/download/`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
        'X-Tenant': tenant,
      },
    });
    // Try direct path too
    const res = response.ok ? response : await fetch(`/records/api/document/${documentId}/download/`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
        'X-Tenant': tenant,
      },
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition');
    let filename = 'document';
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  async deleteDocument(
    documentId: string
  ): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/document/${documentId}/delete/`, {
      method: 'DELETE',
    });
  },

  // ━━━ Patient Records (all-in-one) ━━━

  async getPatientRecords(patientId: string): Promise<{
    medical_records: MedicalRecord[];
    documents: PatientDocument[];
    treatments: ClinicalTreatment[];
    dental_chart?: DentalChartData;
  }> {
    const response = await fetchApi(`records/api/patient/${patientId}/`);
    return extractData<any>(response);
  },

  // ━━━ Legacy Treatment Types (from appointments module) ━━━

  async getTreatmentTypes(): Promise<any[]> {
    try {
      const response = await fetchApi('appointments/api/treatment-types/');
      return extractData<any[]>(response, 'data') || [];
    } catch {
      return [];
    }
  },

  // ━━━ Settings: Acts & Categories CRUD ━━━

  async getSettingsActs(includeInactive = true): Promise<NGAPAct[]> {
    try {
      const response = await fetchApi(
        `records/api/settings/acts/?include_inactive=${includeInactive}`
      );
      return extractData<NGAPAct[]>(response, 'acts');
    } catch (error) {
      console.error('[Records] Error fetching settings acts:', error);
      return [];
    }
  },

  async createAct(data: {
    code: string;
    label: string;
    label_ar?: string;
    label_en?: string;
    category_id?: string;
    coefficient?: number | null;
    default_price?: number | null;
    chart_state_code?: string;
    target_zone?: string;
    duration_minutes?: number | null;
    requires_tooth?: boolean;
    is_active?: boolean;
    sort_order?: number;
  }): Promise<NGAPAct> {
    const response = await fetchApi('records/api/settings/acts/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return extractData<NGAPAct>(response, 'act');
  },

  async updateAct(actId: string, data: Partial<{
    code: string;
    label: string;
    label_ar: string;
    label_en: string;
    category_id: string;
    coefficient: number | null;
    default_price: number | null;
    chart_state_code: string;
    target_zone: string;
    duration_minutes: number | null;
    requires_tooth: boolean;
    is_active: boolean;
    sort_order: number;
  }>): Promise<NGAPAct> {
    const response = await fetchApi(`records/api/settings/acts/${actId}/update/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return extractData<NGAPAct>(response, 'act');
  },

  async toggleAct(actId: string): Promise<{ is_active: boolean; message: string }> {
    const response = await fetchApi(`records/api/settings/acts/${actId}/toggle/`, {
      method: 'POST',
    });
    return response as { is_active: boolean; message: string };
  },

  async deleteAct(actId: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/settings/acts/${actId}/delete/`, {
      method: 'DELETE',
    });
  },

  async getSettingsCategories(): Promise<TreatmentCategory[]> {
    try {
      const response = await fetchApi('records/api/settings/categories/');
      return extractData<TreatmentCategory[]>(response, 'categories');
    } catch (error) {
      console.error('[Records] Error fetching settings categories:', error);
      return [];
    }
  },

  async createCategory(data: {
    name: string;
    name_ar?: string;
    name_en?: string;
    code?: string;
    color?: string;
    icon?: string;
    sort_order?: number;
    is_active?: boolean;
  }): Promise<TreatmentCategory> {
    const response = await fetchApi('records/api/settings/categories/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return extractData<TreatmentCategory>(response, 'category');
  },

  async updateCategory(categoryId: string, data: Partial<{
    name: string;
    name_ar: string;
    name_en: string;
    code: string;
    color: string;
    icon: string;
    sort_order: number;
    is_active: boolean;
  }>): Promise<TreatmentCategory> {
    const response = await fetchApi(`records/api/settings/categories/${categoryId}/update/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return extractData<TreatmentCategory>(response, 'category');
  },

  async deleteCategory(categoryId: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`records/api/settings/categories/${categoryId}/delete/`, {
      method: 'DELETE',
    });
  },

  async uploadCategoryIcon(categoryId: string, file: File): Promise<TreatmentCategory> {
    const formData = new FormData();
    formData.append('icon_image', file);
    const response = await fetchApi(`records/api/settings/categories/${categoryId}/upload-icon/`, {
      method: 'POST',
      body: formData,
    });
    return extractData<TreatmentCategory>(response, 'category');
  },

  async deleteCategoryIcon(categoryId: string): Promise<TreatmentCategory> {
    const response = await fetchApi(`records/api/settings/categories/${categoryId}/delete-icon/`, {
      method: 'DELETE',
    });
    return extractData<TreatmentCategory>(response, 'category');
  },

  async uploadActIcon(actId: string, file: File): Promise<NGAPAct> {
    const formData = new FormData();
    formData.append('icon_image', file);
    const response = await fetchApi(`records/api/settings/acts/${actId}/upload-icon/`, {
      method: 'POST',
      body: formData,
    });
    return extractData<NGAPAct>(response, 'act');
  },

  async deleteActIcon(actId: string): Promise<NGAPAct> {
    const response = await fetchApi(`records/api/settings/acts/${actId}/delete-icon/`, {
      method: 'DELETE',
    });
    return extractData<NGAPAct>(response, 'act');
  },
};